import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { alpha, useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import Iconify from 'src/components/iconify';
import { PlanTier } from 'src/constants/PlanTier';

const TIER_CONFIGS = {
  [PlanTier.STANDARD]: {
    paletteKey: 'info',
    icon: 'solar:rocket-bold', // 🚀 Rocket — Standard
    label: 'Standard',
  },
  [PlanTier.PREMIUM]: {
    paletteKey: 'secondary',
    icon: 'solar:star-circle-bold', // ⭐ Star circle — Premium
    label: 'Premium',
    iconSize: 17,
  },
  [PlanTier.ENTERPRISE]: {
    paletteKey: 'success',
    icon: 'solar:case-bold', // 💼 Briefcase — Enterprise
    label: 'Enterprise',
  },
};

/**
 * PlanBadge — Shows a small icon next to a feature indicating its plan tier.
 *
 * isLocked=true  → lock icon (feature blocked, requires upgrade)
 * isLocked=false → plan tier icon (trial mode: feature accessible but belongs to this plan)
 *
 * Callers should only render this when shouldShowBadge=true (from usePlanFeature).
 * FREE features are not badged (returns null).
 */
export default function PlanBadge({ plan, isLocked, variant = 'mini', sx }) {
  const theme = useTheme();

  if (!plan) return null;

  const planUpper = plan.toUpperCase();
  const config = TIER_CONFIGS[planUpper];

  if (!config) {
    return null; // Don't render badge for FREE or other unrecognized tiers
  }

  const palette = theme.palette[config.paletteKey];
  const colors = {
    bg: alpha(palette.main, 0.12),
    text: palette.dark,
    border: palette.main,
    icon: config.icon,
    label: config.label,
    iconSize: config.iconSize || 14,
  };

  // ── Locked: feature is blocked — show lock icon ──
  if (isLocked) {
    return (
      <Tooltip title={`Available in ${colors.label} Plan`} placement="top" arrow>
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.disabled',
            cursor: 'pointer',
            verticalAlign: 'middle',
            '&:hover': {
              color: colors.text,
            },
            ...sx,
          }}
        >
          <Iconify icon="solar:lock-bold" width={14} height={14} />
        </Box>
      </Tooltip>
    );
  }

  // ── Unlocked trial badge: feature is accessible but belongs to this plan tier.
  // Shown during trial so users understand which plan the feature is part of. ──
  return (
    <Tooltip title={`${colors.label} Plan feature`} placement="top" arrow>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: colors.text,
          cursor: 'default',
          verticalAlign: 'middle',
          opacity: 0.8,
          '&:hover': {
            opacity: 1,
          },
          ...sx,
        }}
      >
        <Iconify icon={colors.icon} width={colors.iconSize} height={colors.iconSize} />
      </Box>
    </Tooltip>
  );
}

PlanBadge.propTypes = {
  plan: PropTypes.string,
  isLocked: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'mini']),
  sx: PropTypes.object,
};

